import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Plus, Receipt } from 'lucide-react';
import { ExpenseList } from '@/components/financials/ExpenseList';
import { ExpenseForm } from '@/components/financials/ExpenseForm';
import type { Expense } from '@/types/financials';

export const Route = createFileRoute('/_app/financials/expenses')({
  component: ExpensesPage,
});

function ExpensesPage() {
  const { farmId, isLoading: isFarmLoading } = useCurrentFarm();
  const queryClient = useQueryClient();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);

  function handleAdd() {
    setEditingExpense(null);
    setDrawerOpen(true);
  }

  function handleEdit(expense: Expense) {
    setEditingExpense(expense);
    setDrawerOpen(true);
  }

  function handleMutationSuccess() {
    queryClient.invalidateQueries({ queryKey: ['financials'] });
  }

  if (isFarmLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!farmId) {
    return (
      <Card>
        <Card.Body className="text-center py-12">
          <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No farm selected.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Expenses</h2>
          <p className="text-sm text-gray-500">Manual expenses and cost adjustments</p>
        </div>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={handleAdd}
        >
          Add Expense
        </Button>
      </div>

      {/* Expense list */}
      <ExpenseList
        farmId={farmId}
        onEdit={handleEdit}
        onMutationSuccess={handleMutationSuccess}
      />

      {/* Expense form drawer */}
      <ExpenseForm
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingExpense(null);
        }}
        farmId={farmId}
        expense={editingExpense}
        onSuccess={handleMutationSuccess}
      />
    </div>
  );
}
